import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { ColumnsType } from 'antd/lib/table'
import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { ISerpPresenceFilter } from '../../model/types/SERPPresenceFilter.interface'
import { selectSerpPresenceFilters } from '../../model/serpPresenceSlice'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { SerpPresenceTableExpandRow } from '../serp-presence-table-expand-row/SerpPresenceTableExpandRow'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import {
  BrandPresenceItems,
  CommaLists,
  GetResearchBrandPresenceApiArg,
  useGetResearchBrandPresenceQuery,
} from '../../../../app/api'
import { PaidResultsCell } from '../../ui/cells/PaidResultsCell'
import { OrganicResultsCell } from '../../ui/cells/OrganicResultsCell'
import clsx from 'clsx'
import styles from './SerpPresenceTable.module.scss'
import { useMenuLayout } from '../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { ActionsPortal } from '../../../../shared/components/utility/actions-slot/ActionsSlot'
import { SerpPresenceActions } from '../serp-presence-actions/SerpPresenceActions'

const Table = ({ onExpandRow, expandedRowKeys }: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const { menuLayout } = useMenuLayout()
  const [countRows, setCountRows] = useState<number>(0)
  const detailedInfoFilter = useAppSelector<ISerpPresenceFilter>(selectSerpPresenceFilters)
  const {
    project,
    country,
    engine_id,
    dateRange: date,
    keyword,
    page_url,
    no_serp_presence,
    is_seo,
  } = detailedInfoFilter

  const [queryParams, setQueryParams] =
    useState<Partial<CommaLists<GetResearchBrandPresenceApiArg>>>()
  const { data, isLoading, isFetching } = useGetResearchBrandPresenceQuery(
    queryParams as GetResearchBrandPresenceApiArg,
    { skip: !project || queryParams == undefined },
  )

  useEffect(() => {
    if (!project) return

    setQueryParams({
      page: 1,
      'per-page': 10,
      projectId: +project,
      countryId: country ? +country : undefined,
      engineId: +engine_id,
      date,
      keywordId: keyword || undefined,
      noSerpPresence: no_serp_presence,
      landingId: page_url || undefined,
    })
  }, [project, country, engine_id, date, keyword, page_url, no_serp_presence])

  useEffect(() => {
    if (data?.data) setCountRows(data.data.total)
  }, [data])

  const columns: ColumnsType<BrandPresenceItems> = [
    {
      title: t('table.columns.keyword'),
      dataIndex: 'keyword',
      key: 'keyword',
    },
    {
      title: t('table.columns.paidResults'),
      dataIndex: 'paid_count',
      key: 'paid_count',
      sorter: true,
      render: (_: string, record) => (
        <PaidResultsCell
          {...record}
          expandedRowKeys={expandedRowKeys}
          is_seo={is_seo}
          onExpandRow={onExpandRow}
        />
      ),
    },
    {
      title: t('table.columns.organicResults'),
      dataIndex: 'organic_count',
      key: 'organic_count',
      sorter: true,
      render: (_: string, record) => (
        <OrganicResultsCell
          {...record}
          expandedRowKeys={expandedRowKeys}
          is_seo={is_seo}
          onExpandRow={onExpandRow}
        />
      ),
    },
    {
      title: t('table.columns.firstUpdated'),
      dataIndex: 'first_updated',
      key: 'first_updated',
      width: '150px',
      sorter: true,
    },
    {
      title: t('table.columns.lastUpdated'),
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 150,
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<BrandPresenceItems> | SorterResult<BrandPresenceItems>[],
  ) => {
    if (queryParams) {
      setQueryParams({
        ...queryParams,
        page: current,
        'per-page': pageSize,
        sort: useSorter(sorter),
      })
    }
  }

  const actions = <SerpPresenceActions queryParams={queryParams} />

  return (
    <section className={clsx(styles.root, { [styles.headless]: menuLayout !== 'top' })}>
      {menuLayout === 'top' ? (
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t('pages.onSiteBrandMentions.onSiteBrandMentionsCount', { count: countRows })}
            <BpTooltip title={t('pages.onSiteBrandMentions.onSiteBrandMentionsTooltip')} />
          </h3>
          {actions}
        </div>
      ) : (
        // В боковом меню заголовка нет — действия уезжают в строку с чипами фильтров
        <ActionsPortal>{actions}</ActionsPortal>
      )}
      <BpTable
        expandable={{
          expandedRowRender: (record: BrandPresenceItems) => (
            <SerpPresenceTableExpandRow record={record} />
          ),
          expandedRowKeys: expandedRowKeys,
          showExpandColumn: false,
        }}
        columns={columns}
        data={data as TTableResponse<BrandPresenceItems>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => `${record.keyword_id}`}
        // в верхнем меню над таблицей заголовок с margin-bottom 16px, в боковом — gap колонки страницы
        stubGap={menuLayout === 'top' ? 16 : 24}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      ></BpTable>
    </section>
  )
}

export const SerpPresenceTable = withExpanded(Table)
